import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText } from 'lucide-react';
import { insurancePolicyService, insuranceClaimService } from '../../lib/services.insurance';
import { admissionService } from '../../lib/services.ipd';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

export default function ClaimForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState<any[]>([]);
  const [policies, setPolicies] = useState<any[]>([]);
  const [admissions, setAdmissions] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    patient_id: '',
    policy_id: '',
    invoice_id: '',
    admission_id: '',
    treatment_date: new Date().toISOString().split('T')[0],
    diagnosis_code: '',
    treatment_code: '',
    billed_amount: '',
    notes: ''
  });

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    if (formData.patient_id) {
      loadPolicies(formData.patient_id);
      loadAdmissions(formData.patient_id);
    }
  }, [formData.patient_id]);

  useEffect(() => {
    if (formData.admission_id) {
      loadInvoices(formData.admission_id);
    }
  }, [formData.admission_id]);

  const loadPatients = async () => {
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setPatients(data || []);
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    }
  };

  const loadPolicies = async (patientId: string) => {
    try {
      const data = await insurancePolicyService.getByPatient(patientId);
      setPolicies(data.filter(p => p.status === 'active'));
    } catch (error) {
      console.error('Failed to load policies:', error);
      toast.error('Failed to load insurance policies');
    }
  };

  const loadAdmissions = async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from('admissions')
        .select('*')
        .eq('patient_id', patientId)
        .order('admission_date', { ascending: false });
      
      if (error) throw error;
      setAdmissions(data || []);
    } catch (error) {
      console.error('Failed to load admissions:', error);
    }
  };

  const loadInvoices = async (admissionId: string) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('reference_id', admissionId)
        .eq('reference_type', 'admission');
      
      if (error) throw error;
      setInvoices(data || []);
      
      if (data && data.length > 0) {
        setFormData(prev => ({
          ...prev,
          invoice_id: data[0].id,
          billed_amount: data[0].total_amount?.toString() || ''
        }));
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.policy_id || !formData.billed_amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const claim = await insuranceClaimService.create({
        policy_id: formData.policy_id,
        invoice_id: formData.invoice_id || undefined,
        admission_id: formData.admission_id || undefined,
        claim_date: new Date().toISOString().split('T')[0],
        treatment_date: formData.treatment_date,
        diagnosis_code: formData.diagnosis_code || undefined,
        treatment_code: formData.treatment_code || undefined,
        billed_amount: parseFloat(formData.billed_amount),
        status: 'draft',
        notes: formData.notes || undefined
      });

      if (selectedFile && claim.id) {
        await insuranceClaimService.uploadDocument(claim.id, selectedFile);
      }

      toast.success('Insurance claim created successfully');
      navigate('/insurance/claims');
    } catch (error) {
      console.error('Failed to create claim:', error);
      toast.error('Failed to create insurance claim');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/insurance/claims')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Claims
        </button>
        <h1 className="text-2xl font-bold text-gray-900">New Insurance Claim</h1>
        <p className="text-gray-600">Submit a new insurance claim for patient treatment</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient *
            </label>
            <select
              value={formData.patient_id}
              onChange={(e) => setFormData({ ...formData, patient_id: e.target.value, policy_id: '', admission_id: '', invoice_id: '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select patient</option>
              {patients.map(patient => (
                <option key={patient.id} value={patient.id}>{patient.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Insurance Policy *
            </label>
            <select
              value={formData.policy_id}
              onChange={(e) => setFormData({ ...formData, policy_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={!formData.patient_id}
            >
              <option value="">Select policy</option>
              {policies.map(policy => (
                <option key={policy.id} value={policy.id}>
                  {policy.insurance_company} - {policy.policy_number}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admission (Optional)
            </label>
            <select
              value={formData.admission_id}
              onChange={(e) => setFormData({ ...formData, admission_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!formData.patient_id}
            >
              <option value="">Select admission</option>
              {admissions.map(admission => (
                <option key={admission.id} value={admission.id}>
                  {admission.admission_no} - {new Date(admission.admission_date).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invoice (Optional)
            </label>
            <select
              value={formData.invoice_id}
              onChange={(e) => setFormData({ ...formData, invoice_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!formData.admission_id || invoices.length === 0}
            >
              <option value="">Select invoice</option>
              {invoices.map(invoice => (
                <option key={invoice.id} value={invoice.id}>
                  {invoice.invoice_number} - ${invoice.total_amount}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Treatment Date *
            </label>
            <input
              type="date"
              value={formData.treatment_date}
              onChange={(e) => setFormData({ ...formData, treatment_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Billed Amount *
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.billed_amount}
              onChange={(e) => setFormData({ ...formData, billed_amount: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Diagnosis Code
            </label>
            <input
              type="text"
              value={formData.diagnosis_code}
              onChange={(e) => setFormData({ ...formData, diagnosis_code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., ICD-10 code"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Treatment Code
            </label>
            <input
              type="text"
              value={formData.treatment_code}
              onChange={(e) => setFormData({ ...formData, treatment_code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., CPT code"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Additional information about the claim..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supporting Documents
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              onChange={handleFileChange}
              className="hidden"
              id="claim-document"
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <label
              htmlFor="claim-document"
              className="cursor-pointer flex flex-col items-center"
            >
              {selectedFile ? (
                <>
                  <FileText className="w-12 h-12 text-blue-600 mb-2" />
                  <span className="text-sm text-gray-700">{selectedFile.name}</span>
                  <span className="text-xs text-gray-500 mt-1">Click to change</span>
                </>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-700">Click to upload document</span>
                  <span className="text-xs text-gray-500 mt-1">PDF, JPG, PNG up to 10MB</span>
                </>
              )}
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-4 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/insurance/claims')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Claim'}
          </button>
        </div>
      </form>
    </div>
  );
}
